/**
 * Telemetry tab (`ldt-telemetry`, ENG-232) — every value the aircraft's source
 * streams, grouped and collapsible like the Flight Ops tab. Which rows appear
 * depends on the source family (MAVLink / DJI+FH2 / DroneSense / LiveADS); see
 * `lib/telemetry/rows.ts`.
 */
import { InfoRow } from '@/components/InfoRow';
import { SectionGroup } from '@/components/Section';
import { Accordion } from '@/components/ui/accordion';
import { type DroneLive, telemetryFamily } from '@/lib/store/drones';
import { useUiStore } from '@/lib/store/ui';
import { buildTelemetryGroups, filledCount } from '@/lib/telemetry/rows';

export function TelemetryTab({ drone }: { drone: DroneLive }) {
  const t = drone.telemetry;
  const groups = buildTelemetryGroups(t, telemetryFamily(t));
  const groupsOpen = useUiStore((s) => s.telemetryGroupsOpen);
  const setGroupOpen = useUiStore((s) => s.setTelemetryGroupOpen);
  return (
    <SectionGroup>
      {groups.map((g) => (
        <Accordion
          key={g.id}
          title={g.title}
          meta={filledCount(g)}
          open={groupsOpen[g.id] ?? true}
          onOpenChange={(open) => setGroupOpen(g.id, open)}
        >
          <div className="space-y-1">
            {g.rows.map((r) => (
              <InfoRow
                key={r.key}
                label={r.label}
                value={r.sub ? `${r.value} · ${r.sub}` : r.value}
                mono
                valueClassName={
                  r.tone === 'ok'
                    ? 'text-(--color-green)'
                    : r.tone === 'crit'
                      ? 'text-(--color-red)'
                      : undefined
                }
              />
            ))}
          </div>
        </Accordion>
      ))}
    </SectionGroup>
  );
}
